import React, { useEffect, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import api from '@/api/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('pt-PT');
  } catch {
    return value;
  }
};

const formatCurrency = (value) => `€${Number(value || 0).toFixed(2)}`;

const getStatusBadge = (status) => {
  const map = {
    pending: 'bg-amber-100 text-amber-800',
    in_progress: 'bg-blue-100 text-blue-800',
    completed: 'bg-emerald-100 text-emerald-800',
    cancelled: 'bg-muted text-muted-foreground',
  };
  return map[status] || 'bg-muted text-muted-foreground';
};

const getStatusText = (status) => {
  const map = { pending: 'Pendente', in_progress: 'Em Progresso', completed: 'Concluída', cancelled: 'Cancelada' };
  return map[status] || status;
};

export default function ServiceOrderManagement() {
  const [orders, setOrders] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterVehicle, setFilterVehicle] = useState('');
  const [formData, setFormData] = useState({
    vehicle_id: '',
    partner_id: '',
    service_type: '',
    description: '',
    scheduled_date: '',
    estimated_cost: '',
  });

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterVehicle) params.vehicle_id = filterVehicle;
      const [ordersRes, vehiclesRes, partnersRes] = await Promise.all([
        api.get('/service-orders', { params }),
        api.get('/vehicles'),
        api.get('/partners'),
      ]);
      setOrders(ordersRes.data?.data || []);
      setVehicles(vehiclesRes.data?.data || []);
      setPartners(partnersRes.data?.data || []);
    } catch {
      toast.error('Erro ao carregar ordens de serviço');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterVehicle]);

  const resetForm = () => {
    setFormData({
      vehicle_id: '',
      partner_id: '',
      service_type: '',
      description: '',
      scheduled_date: '',
      estimated_cost: '',
    });
    setEditing(null);
  };

  const openEdit = (order) => {
    setEditing(order);
    setFormData({
      vehicle_id: order.vehicle_id,
      partner_id: order.partner_id || '',
      service_type: order.service_type,
      description: order.description || '',
      scheduled_date: order.scheduled_date ? order.scheduled_date.split('T')[0] : '',
      estimated_cost: order.estimated_cost || '',
    });
    setShowModal(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy('save');
    try {
      if (editing) {
        await api.put(`/service-orders/${editing.id}`, formData);
        toast.success('Ordem atualizada');
      } else {
        await api.post('/service-orders', formData);
        toast.success('Ordem criada');
      }
      setShowModal(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao guardar ordem');
    } finally {
      setBusy(null);
    }
  };

  const complete = async (id) => {
    const cost = window.prompt('Custo final (€):');
    if (!cost) return;
    setBusy(id);
    try {
      await api.post(`/service-orders/${id}/complete`, { actual_cost: cost });
      toast.success('Ordem concluída');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao concluir ordem');
    } finally {
      setBusy(null);
    }
  };

  const cancel = async (id) => {
    const reason = window.prompt('Motivo do cancelamento:');
    if (!reason) return;
    setBusy(id);
    try {
      await api.post(`/service-orders/${id}/cancel`, { reason });
      toast.success('Ordem cancelada');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao cancelar ordem');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
        <p className="mt-3 text-sm text-muted-foreground">Carregando Ordens de Serviço...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Ordens de Serviço</h1>
        <Button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          <Plus size={16} className="mr-2" /> Nova Ordem
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="text-sm font-medium">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              >
                <option value="">Todos</option>
                <option value="pending">Pendente</option>
                <option value="in_progress">Em Progresso</option>
                <option value="completed">Concluída</option>
                <option value="cancelled">Cancelada</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Veículo</label>
              <select
                value={filterVehicle}
                onChange={(e) => setFilterVehicle(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              >
                <option value="">Todos</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.license_plate}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setFilterStatus('');
                  setFilterVehicle('');
                }}
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ordens</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2">Veículo</th>
                  <th>Tipo</th>
                  <th>Parceiro</th>
                  <th>Data</th>
                  <th className="text-right">Custo Est.</th>
                  <th>Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b">
                    <td className="py-2 font-medium">{o.vehicle_plate}</td>
                    <td>{o.service_type}</td>
                    <td>{o.partner_name || '—'}</td>
                    <td>{formatDate(o.scheduled_date)}</td>
                    <td className="text-right">{formatCurrency(o.estimated_cost)}</td>
                    <td>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${getStatusBadge(o.status)}`}>
                        {getStatusText(o.status)}
                      </span>
                    </td>
                    <td className="text-right">
                      {(o.status === 'pending' || o.status === 'in_progress') && (
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(o)}>
                            Editar
                          </Button>
                          <Button size="sm" onClick={() => complete(o.id)} disabled={busy === o.id}>
                            {busy === o.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Concluir'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => cancel(o.id)} disabled={busy === o.id}>
                            Cancelar
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr>
                    <td colSpan="7" className="py-4 text-center text-sm text-muted-foreground">
                      Nenhuma ordem de serviço.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{editing ? 'Editar Ordem' : 'Nova Ordem'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium">Veículo *</label>
                    <select
                      value={formData.vehicle_id}
                      onChange={(e) => setFormData({ ...formData, vehicle_id: e.target.value })}
                      required
                      className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                    >
                      <option value="">Selecione...</option>
                      {vehicles.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.license_plate}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Parceiro</label>
                    <select
                      value={formData.partner_id}
                      onChange={(e) => setFormData({ ...formData, partner_id: e.target.value })}
                      className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                    >
                      <option value="">Selecione...</option>
                      {partners.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.company_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Tipo de Serviço *</label>
                    <Input
                      value={formData.service_type}
                      onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                      required
                      placeholder="Ex: Revisão"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Data Agendada</label>
                    <Input
                      type="date"
                      value={formData.scheduled_date}
                      onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Custo Estimado (€)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.estimated_cost}
                      onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Descrição</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    placeholder="Detalhes do serviço..."
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={busy === 'save'} className="flex-1">
                    {busy === 'save' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : editing ? 'Atualizar' : 'Criar'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
